import React from 'react';

import { CtaCard as Card, Container, CtaCardProps, CtaIcon } from '@nakedplatform/ui';
import { getGuestBasketHash } from '../../../basket/helpers/storage';

import { FakeChatAnimated } from '../fake-chat-animated';
import { MessageList } from '../message-list';
import * as Constants from './constants';

/**
 * Compiles the list of messages shown to the user in the fake chat
 *
 * @param noBasketGreeting - The message that Rose greets the user with if they don't have a basket
 * @param basketGreeting - The message that Rose greets the user with if they already have a basket
 * @param messages - The messages that Rose sends the user to prompt to prompt them to action
 * @param userHasBasket - Whether or not the user already has a basket
 */
function getMessages(
  noBasketGreeting: string,
  basketGreeting: string,
  messages: string[],
  userHasBasket: boolean
) {
  if (userHasBasket) {
    return [basketGreeting, ...messages];
  }

  return [noBasketGreeting, ...messages];
}
/**
 * A CtaCard with its margins and dimensions set to properly render on the start quote page specifically
 *
 * @param props - the properties used by the CtaCard component
 */
function CtaCard(props: CtaCardProps) {
  return (
    <Card
      width={[Constants.MOBILE_CARD_WIDTH]}
      height={Constants.CARD_HEIGHT}
      mt={[Constants.CARD_MARGIN_BETWEEN]}
      {...props}
    />
  );
}
/**
 * The first screen the user sees when quoting. From here, they can select which type of insurance they would like to get a quote for.
 *
 * @param props - Props passed to this component
 */
export function Main(props: MainProps) {
  const {
    goToCar,
    goToHomeContents,
    goToSingleItem,
    goToBuilding,
    goToTowable,
    goToPet,
    showPetQuoteCta,
  } = props;
  const userHasBasket = !!getGuestBasketHash();

  const messages = getMessages(
    Constants.ROSE_NO_BASKET_GREETING,
    Constants.ROSE_BASKET_GREETING,
    Constants.messages,
    userHasBasket
  );

  return (
    <FakeChatAnimated>
      <MessageList messages={messages} />
      <Container
        display="flex"
        flexDirection={['column']}
        mt={[Constants.CARD_SECTION_TOP_MARGIN_MOBILE, Constants.CARD_SECTION_TOP_MARGIN]}
        width={[Constants.CARD_WIDTH_MOBILE, Constants.CARD_WIDTH_TABLET, Constants.CARD_WIDTH]}
      >
        <CtaCard text="Car" icon={<CtaIcon name="cta-car" />} onPress={goToCar} />
        <CtaCard
          text="Home contents"
          icon={<CtaIcon name="cta-contents" />}
          onPress={goToHomeContents}
        />
        {showPetQuoteCta ? (
          <CtaCard text="Pet" icon={<CtaIcon name="cta-pet" />} onPress={goToPet} />
        ) : null}
        <CtaCard text="Building" icon={<CtaIcon name="cta-building" />} onPress={goToBuilding} />
        <CtaCard
          text="A single item"
          icon={<CtaIcon name="cta-single-items" />}
          onPress={goToSingleItem}
        />
        <CtaCard
          text="Caravan or trailer"
          icon={<CtaIcon name="cta-towable" />}
          onPress={goToTowable}
        />
      </Container>
    </FakeChatAnimated>
  );
}
export interface MainProps {
  // when true, the Pet insurance quote card is shown
  showPetQuoteCta?: boolean;
  // the function to call when the user clicks on a link that would take them to quote for car insurance
  goToCar: () => void;
  // the function to call when the user clicks on a link that would take them to quote for single item insurance
  goToSingleItem: () => void;
  // the function to call when the user clicks on a link that would take them to quote for home contents insurance
  goToHomeContents: () => void;
  // the function to call when the user clicks on a link that would take them to quote for building insurance
  goToBuilding: () => void;
  // the function to call when the user clicks on a link that would take them to quote for towable insurance
  goToTowable: () => void;
  // the function to call when the user clicks on a link that would take them to quote for pet insurance
  goToPet: () => void;
}
